import { useMutation } from '@apollo/client';
import { useState } from 'react';

import Button from '@/componentsv2/Button';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import { formatDateShort } from '../components/EngagementHub/format';
import type { ViewerRole } from '../components/EngagementHub/roles';
import {
  canConfirmAmendmentFor,
  canProposeAmendmentFor,
  canWithdrawAmendmentFor,
  hasOpenAmendment,
} from '../components/EngagementHub/roles';
import Section from './Section';
import UpdateEngagementDrawer from './UpdateEngagementDrawer';
import { fieldEntriesLive, sharedChangeSetOf } from './amendments/fieldEntriesLive';
import { CONFIRM_ENGAGEMENT_AMENDMENT, WITHDRAW_ENGAGEMENT_AMENDMENT } from './operations';
import { partyNamer } from './parties';
import { useSelfIdentity } from './session';
import type { AmendmentView, EngagementView } from './viewModel';

interface AmendmentsPanelProps {
  view: EngagementView;
  viewerRole: ViewerRole;
  viewerUserId: string;
}

/**
 * The five persisted statuses (`amendment.go:69-75`). `proposed` and
 * `pending_governance` are the live pair; the other three are absorbing, and all
 * three reach this panel — a countered or withdrawn amendment is history too,
 * not an amendment that never happened.
 */
const STATUS_LABEL: Record<string, string> = {
  proposed: 'Awaiting a response',
  pending_governance: 'Parked for governance approval',
  accepted: 'Accepted',
  countered: 'Countered',
  withdrawn: 'Withdrawn',
};

/** Open = still blocking a new proposal (the api allows one live amendment). */
function isOpen(a: AmendmentView): boolean {
  return a.status === 'proposed' || a.status === 'pending_governance';
}

/**
 * Counter prefill: a counter SUPERSEDES the amendment it answers (the api marks
 * it `countered`), so the form starts from the proposal's own values — leaving
 * a field as prefilled keeps that part of the proposal; an empty form would
 * silently drop it. Only the §6.3-shared keys exist to prefill: a rate is never
 * counterable by the other side (redacted for the customer, and a customer
 * proposal cannot carry one for the expert to answer).
 */
function counterInitial(a: AmendmentView): { nteHours?: string; scope?: string } {
  const change = sharedChangeSetOf(a);
  return {
    ...(change.nte_hours_total !== undefined ? { nteHours: change.nte_hours_total } : {}),
    ...(change.scope !== undefined ? { scope: change.scope } : {}),
  };
}

/**
 * The Amendments tab: the live proposal and what can be done to it, above
 * everything that has already been settled.
 *
 * **This is where an amendment is proposed from**, not the `ActionStrip` — the
 * strip owns the offer lifecycle (make / accept / decline / withdraw), this panel
 * owns the amendment loop.
 *
 * History is every settled amendment, not only the accepted ones: a counter
 * resolves its predecessor as `countered` and chains a successor
 * (`supersedesAmendmentId`), so filtering to `accepted` would collapse a
 * negotiation to whichever link won and hide a withdrawn proposal entirely.
 *
 * `pending_governance` is **amendment-row state, not engagement state**: the
 * engagement never leaves `active` while an amendment is parked, so the frame
 * says the amendment is waiting, never that the engagement is. Governance
 * approval happens outside this panel — and today has no read surface at all
 * (the approvals queue can't see parked amendments; ENG-5612) — so the only
 * action offered here is the proposer withdrawing it.
 */
export default function AmendmentsPanel({
  view,
  viewerRole,
  viewerUserId,
}: AmendmentsPanelProps): JSX.Element {
  const [drawer, setDrawer] = useState<{ counterOf: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmAmendment, { loading: confirming }] = useMutation(CONFIRM_ENGAGEMENT_AMENDMENT, {
    refetchQueries: ['engagementHub'],
  });
  const [withdrawAmendment, { loading: withdrawing }] = useMutation(WITHDRAW_ENGAGEMENT_AMENDMENT, {
    refetchQueries: ['engagementHub'],
  });

  const self = useSelfIdentity();
  const namer = partyNamer(view, viewerUserId, [], self);
  const open = view.amendments.find(isOpen) ?? null;
  // Oldest-first on the wire; the audit trail reads most-recent-first.
  const settled = view.amendments.filter((a) => !isOpen(a)).reverse();

  const canPropose =
    canProposeAmendmentFor(view.status, viewerRole) && !hasOpenAmendment(view.amendments);

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? fallback);
    }
  }

  const canConfirm =
    open !== null && canConfirmAmendmentFor(view.status, open, viewerRole, view.expertId);
  const canWithdraw = open !== null && canWithdrawAmendmentFor(view.status, open, viewerUserId);

  return (
    <Section title="Amendments">
      <div className="flex flex-col gap-24">
        {open ? (
          <div className="flex flex-col gap-12">
            <AmendmentCard
              amendment={open}
              currency={view.currency}
              proposerName={namer(open.proposedByUserId)}
              highlighted
            />
            <div className="flex flex-wrap justify-end gap-8">
              {canConfirm ? (
                <>
                  <Button
                    size="small"
                    disabled={confirming}
                    onClick={() =>
                      void run(
                        () => confirmAmendment({ variables: { input: { amendmentId: open.id } } }),
                        'This amendment could not be accepted.'
                      )
                    }
                  >
                    Accept
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setDrawer({ counterOf: open.id })}
                  >
                    Counter
                  </Button>
                </>
              ) : null}
              {canWithdraw ? (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={withdrawing}
                  onClick={() =>
                    void run(
                      () => withdrawAmendment({ variables: { input: { amendmentId: open.id } } }),
                      'This amendment could not be withdrawn.'
                    )
                  }
                >
                  Withdraw
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {canPropose ? (
          <div className="flex justify-end">
            <Button size="small" onClick={() => setDrawer({ counterOf: null })}>
              Propose an amendment
            </Button>
          </div>
        ) : null}

        {error ? <p className="text-red-600 body-2">{error}</p> : null}

        {settled.length === 0 ? (
          <p className="text-on-surface-muted body-2">
            {open ? 'This is the first amendment.' : 'This engagement has not been amended.'}
          </p>
        ) : (
          <div className="flex flex-col gap-16">
            <h3 className="text-on-surface-muted subtitle-2">History</h3>
            {settled.map((a) => (
              <AmendmentCard
                key={a.id}
                amendment={a}
                currency={view.currency}
                proposerName={namer(a.proposedByUserId)}
              />
            ))}
          </div>
        )}
      </div>

      {drawer !== null ? (
        <UpdateEngagementDrawer
          open
          onClose={() => setDrawer(null)}
          view={view}
          // Only the expert may set the expert rate (§6.3).
          canSetRate={viewerRole === 'expert'}
          counterOfAmendmentId={drawer.counterOf}
          initial={drawer.counterOf !== null && open !== null ? counterInitial(open) : undefined}
        />
      ) : null}
    </Section>
  );
}

interface AmendmentCardProps {
  amendment: AmendmentView;
  currency: string | null;
  proposerName: string;
  highlighted?: boolean;
}

function AmendmentCard({ amendment, currency, proposerName, highlighted }: AmendmentCardProps) {
  const entries = fieldEntriesLive(amendment, currency);
  /*
   * Two dates, each next to the event it belongs to: `resolvedAt` is when the
   * amendment LEFT the live set, so it dates the OUTCOME, not the proposal. One
   * caption carrying both would read "Proposed by Robin · Aug 19" on an
   * amendment proposed on the 17th and accepted on the 19th.
   */
  const outcome = STATUS_LABEL[amendment.status] ?? amendment.status;
  const settledOn =
    amendment.resolvedAt === null ? '' : ` · ${formatDateShort(amendment.resolvedAt)}`;

  return (
    <div
      className={`flex flex-col gap-12 rounded-md border px-16 py-16 ${
        highlighted ? 'border-brand-primary bg-surface-elevated' : 'border-surface-border'
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-8">
        <span className="text-on-surface subtitle-2">{`${outcome}${settledOn}`}</span>
        <span className="text-on-surface-muted body-2">
          Proposed by {proposerName} · {formatDateShort(amendment.createdAt)}
        </span>
      </div>

      {entries.length === 0 ? (
        // Every field in this amendment is on the other side of the §6.3 wall.
        // Absence is the correct render — never a zero, never an error.
        <p className="text-on-surface-muted body-2">
          The details of this amendment aren&apos;t visible to you.
        </p>
      ) : (
        <dl className="flex flex-col gap-8">
          {entries.map((entry) => (
            <div key={entry.label} className="flex flex-col gap-2 sm:flex-row sm:gap-16">
              <dt className="text-on-surface-muted subtitle-2 sm:w-[140px] sm:shrink-0">
                {entry.label}
              </dt>
              <dd className="flex flex-col">
                <span className="text-on-surface body-2">{entry.next}</span>
                <span className="text-on-surface-muted body-2">Was: {entry.prior}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
